import type { RefObject } from "react";
import { ReactNode, createContext, useRef, useState } from "react";

import { ActionSheetRef } from "react-native-actions-sheet";

interface BottomSheetContextType {
  showSheet: (id: string, data?: Record<string, any>) => void;
  hideSheet: (id: string) => void;
  registerSheet: (id: string, ref: RefObject<ActionSheetRef>) => void;
  getSheetData: (id: string) => Record<string, any> | undefined;
}

export const BottomSheetContext = createContext<BottomSheetContextType | null>(
  null
);

export const BottomSheetProvider = ({ children }: { children: ReactNode }) => {
  const sheetRefs = useRef(new Map<string, React.RefObject<ActionSheetRef>>());
  const [sheetData, setSheetData] = useState<
    Record<string, Record<string, any>>
  >({});

  const getRef = (id: string) => sheetRefs.current.get(id);

  const registerSheet = (id: string, ref: RefObject<ActionSheetRef>) => {
    sheetRefs.current.set(id, ref);
  };

  const showSheet = (id: string, data?: Record<string, any>) => {
    const ref = getRef(id);

    // Store the data for this sheet if provided
    if (data) {
      setSheetData((prev) => ({
        ...prev,
        [id]: data,
      }));
    }

    ref?.current?.show();
  };

  const hideSheet = (id: string) => {
    const ref = getRef(id);
    ref?.current?.hide();
  };

  const getSheetData = (id: string) => {
    return sheetData[id];
  };

  return (
    <BottomSheetContext.Provider
      value={{ showSheet, hideSheet, registerSheet, getSheetData }}
    >
      {children}
    </BottomSheetContext.Provider>
  );
};
